import React, {
  ChangeEvent,
  FunctionComponent,
  ReactNode,
  useEffect,
  useState,
} from "react";
import { ChoiceList } from "@cmsgov/ds-medicare-gov";
import { ChoiceState } from "./types";

interface MultiSelectChoicesProps {
  name: string;
  label: ReactNode;
  hint?: ReactNode;
  initialChoices: ChoiceState[];
  // value to opt out of making a selection
  optOutValue: string;
  onSelectionChanged: (updatedChoices: ChoiceState[]) => void;
  errorMessage?: ReactNode;
}

/**
 * MultiSelectChoices is a component that displays a list of Choice component with an option for the user to opt out making a selection.
 */
export const MultiSelectChoices: FunctionComponent<MultiSelectChoicesProps> = ({
  name,
  label,
  hint,
  initialChoices,
  optOutValue,
  onSelectionChanged,
  errorMessage,
}) => {
  const [choices, setChoices] = useState<ChoiceState[]>([]);

  useEffect(() => {
    setChoices(initialChoices);
  }, [initialChoices]);

  const handleChoiceSelection = (event: ChangeEvent<HTMLInputElement>) => {
    const currentChecked = event.currentTarget.checked;
    const currentValue = event.currentTarget.value;

    if (currentChecked) {
      if (optOutValue === currentValue) {
        // opt out value is selected, unselect all the other selections.
        const updatedChoices = choices.map(choice => {
          if (optOutValue === choice.value) {
            return { ...choice, checked: true };
          }
          return { ...choice, checked: false };
        });
        setChoices(updatedChoices);
        onSelectionChanged(updatedChoices);
      } else {
        // other selection is checked, uncheck opt out option
        const updatedChoices = choices.map(choice => {
          if (optOutValue === choice.value) {
            return { ...choice, checked: false };
          }
          if (choice.value === currentValue) {
            return { ...choice, checked: currentChecked };
          }
          return choice;
        });
        setChoices(updatedChoices);
        onSelectionChanged(updatedChoices);
      }
    } else {
      // uncheck
      const updatedChoices = choices.map(choice => {
        if (choice.value === currentValue) {
          return { ...choice, checked: currentChecked };
        }
        return choice;
      });
      setChoices(updatedChoices);
      onSelectionChanged(updatedChoices);
    }
  };

  return (
    <ChoiceList
      choices={choices}
      label={label}
      hint={hint}
      name={name}
      type="checkbox"
      errorMessage={errorMessage}
      onChange={handleChoiceSelection}
    />
  );
};
